import { useState, useMemo } from 'react';
import {
  Modal,
  SpaceBetween,
  Button,
  FormField,
  Input,
  Textarea,
  Alert,
  Checkbox,
} from '@cloudscape-design/components';
import { ConfigurationObject } from '../services/configs-api';
import { useDeleteConfig } from '../hooks/use-delete-config';
import { useDependents } from '../hooks/use-dependents';
import { useNotifications } from '@features/notifications';

interface DeleteConfirmationModalProps {
  config: ConfigurationObject;
  visible: boolean;
  onDismiss: () => void;
}

/**
 * Delete confirmation modal with:
 * - Active usage warning (lastAccessedAt within 7 days)
 * - Name-match confirmation input
 * - Mandatory deletion reason (min 10 chars)
 * - "Schedule deletion" button enabled only when both conditions met
 *
 * Requirements: 5.5–5.14, 5.22, 5.23
 */
export function DeleteConfirmationModal({ config, visible, onDismiss }: DeleteConfirmationModalProps) {
  const [typedName, setTypedName] = useState('');
  const [reason, setReason] = useState('');
  const [dependencyAcknowledged, setDependencyAcknowledged] = useState(false);
  const deleteConfig = useDeleteConfig();
  const { data: dependentsData, isError: dependentsError } = useDependents(visible ? config.configId : undefined);
  const { addNotification } = useNotifications();

  const hasDependents = (dependentsData?.count ?? 0) > 0;

  // Check if actively in use (accessed within last 7 days)
  const isActivelyUsed = useMemo(() => {
    if (!config.lastAccessedAt) return false;
    const sevenDaysAgo = Date.now() - 7 * 24 * 60 * 60 * 1000;
    return new Date(config.lastAccessedAt).getTime() > sevenDaysAgo;
  }, [config.lastAccessedAt]);

  // Format "last accessed X ago" text
  const lastAccessedText = useMemo(() => {
    if (!config.lastAccessedAt) return '';
    const diff = Date.now() - new Date(config.lastAccessedAt).getTime();
    const hours = Math.floor(diff / (1000 * 60 * 60));
    if (hours < 24) return `${hours} hour${hours !== 1 ? 's' : ''} ago`;
    const days = Math.floor(hours / 24);
    return `${days} day${days !== 1 ? 's' : ''} ago`;
  }, [config.lastAccessedAt]);

  const nameMatches = typedName === config.configName;
  const reasonValid = reason.length >= 10;
  const canDelete = nameMatches && reasonValid && (!hasDependents || dependencyAcknowledged);

  const handleDelete = async () => {
    try {
      await deleteConfig.mutateAsync({ configId: config.configId, reason });
      addNotification('success', `Configuration '${config.configName}' scheduled for deletion in 24 hours`);
      onDismiss();
    } catch (e) {
      const { translateError } = await import('@features/notifications/error-messages');
      const translated = translateError(e, { configName: config.configName });
      addNotification('error', translated.message, translated.technicalDetails);
    }
  };

  const handleDismiss = () => {
    setTypedName('');
    setReason('');
    onDismiss();
  };

  return (
    <Modal
      visible={visible}
      onDismiss={handleDismiss}
      header="Delete Configuration"
      footer={
        <SpaceBetween direction="horizontal" size="xs">
          <Button onClick={handleDismiss}>Cancel</Button>
          <Button
            variant="primary"
            disabled={!canDelete}
            loading={deleteConfig.isPending}
            onClick={handleDelete}
          >
            Schedule deletion
          </Button>
        </SpaceBetween>
      }
    >
      <SpaceBetween size="m">
        {hasDependents && (
          <Alert type="warning">
            ⚠ This configuration is referenced by {dependentsData!.count} other configuration(s):
            <ul>
              {dependentsData!.dependents.map((d) => (
                <li key={d.configId}>{d.configName} ({d.configType}) — field: {d.referencingField}</li>
              ))}
            </ul>
          </Alert>
        )}

        {dependentsError && (
          <Alert type="info">
            Unable to check for dependencies. Proceed with caution.
          </Alert>
        )}

        {isActivelyUsed ? (
          <Alert type="warning">
            ⚠ This configuration was last accessed {lastAccessedText} and appears to be actively
            in use by Amazon Connect. Deleting it may cause call handling failures.
          </Alert>
        ) : (
          <Alert type="info">
            This configuration will be permanently deleted after a 24-hour grace period. Amazon
            Connect Lambda functions referencing this configuration will continue to work during
            the grace period.
          </Alert>
        )}

        <SpaceBetween size="xs">
          <strong>Name:</strong> {config.configName}
        </SpaceBetween>
        <SpaceBetween size="xs">
          <strong>Type:</strong> {config.configType}
        </SpaceBetween>

        <FormField
          label={`Type "${config.configName}" to confirm`}
          errorText={typedName.length > 0 && !nameMatches ? 'Name does not match' : undefined}
        >
          <Input
            value={typedName}
            onChange={({ detail }) => setTypedName(detail.value)}
            placeholder={config.configName}
          />
        </FormField>

        <FormField
          label="Reason for deletion"
          description="Minimum 10 characters. This will be recorded in the audit log."
          errorText={reason.length > 0 && !reasonValid ? `${10 - reason.length} more characters required` : undefined}
        >
          <Textarea
            value={reason}
            onChange={({ detail }) => setReason(detail.value)}
            placeholder="Explain why this configuration is being deleted..."
            rows={3}
          />
        </FormField>

        {hasDependents && (
          <Checkbox
            checked={dependencyAcknowledged}
            onChange={({ detail }) => setDependencyAcknowledged(detail.checked)}
          >
            I understand this may break dependent configurations
          </Checkbox>
        )}
      </SpaceBetween>
    </Modal>
  );
}
